import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Money is stored as INTEGER PAISE end-to-end (backend storage + API wire, like
// Stripe). inr()/inrFull() take paise and render rupees — they are the single
// display boundary, so server amounts pass straight through. For a value the user
// typed in RUPEES (a live form preview), convert first: inrFull(toPaise(typed)).
export function inr(n) {
  if (n == null || isNaN(n)) return "₹0";
  const num = Number(n) / 100; // paise -> rupees
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

export function inrFull(n) {
  return `₹${(Number(n || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

// Axis-tick variant of inr(): the same Indian-system compaction (L/Cr — never "₹1800K"), kept
// short (≤1 decimal, no space) so chart Y-axis labels stay narrow. Takes paise like inr().
export function inrAxis(n) {
  if (n == null || isNaN(n)) return "₹0";
  const num = Number(n) / 100;
  const fmt = (v) => String(v >= 100 ? Math.round(v) : Math.round(v * 10) / 10);
  if (num >= 10000000) return `₹${fmt(num / 10000000)}Cr`;
  if (num >= 100000) return `₹${fmt(num / 100000)}L`;
  if (num >= 1000) return `₹${fmt(num / 1000)}K`;
  return `₹${Math.round(num)}`;
}

// Register/evidence variant of inrFull(): ALWAYS renders exact paise ("₹0.00", "₹1,234.56" —
// never a dropped ".00"), the sign OUTSIDE the rupee symbol ("-₹1,234.56"), and an em-dash for
// null/blank/non-integer input — the rendering the HR/Payroll money registers established
// (formatPayrollMoney and friends). Takes integer paise like inr()/inrFull(). This is the ONE
// implementation of that vocabulary; keep per-module names as thin delegates to it, never
// re-implementations of the grouping/locale (byte-identity pinned by
// lib/moneyFormatConsolidation.test.js).
export function inrExact(n) {
  if (n == null || n === "") return "—";
  const amount = Number(n);
  if (!Number.isSafeInteger(amount)) return "—";
  const sign = amount < 0 ? "-" : "";
  const absolute = Math.abs(amount);
  const rupees = Math.floor(absolute / 100);
  const paise = absolute % 100;
  return `${sign}₹${rupees.toLocaleString("en-IN", { maximumFractionDigits: 0 })}.${String(paise).padStart(2, "0")}`;
}

// The conversion boundary. Money inputs (rupees the user types) -> toPaise() before
// POST; raw paise -> fromPaise() when you need a rupee number for non-inr math.
export function toPaise(rupees) {
  return Math.round(Number(rupees || 0) * 100);
}

export function fromPaise(paise) {
  return Number(paise || 0) / 100;
}

export function initials(name = "") {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// Person-name helpers — mirror of backend core/names (India-first). The full `name` stays the source of
// truth (Indian names don't split cleanly: mononyms, initials, patronymics), so we DERIVE first/surname/
// display on demand instead of storing a first/last split. `first` = first token; `last` = final token
// (surname) when there's more than one; `full` = trimmed original.
export function splitName(name = "") {
  const full = String(name || "").split(/\s+/).filter(Boolean).join(" ");
  const parts = full ? full.split(" ") : [];
  if (!parts.length) return { first: "", last: "", full: "" };
  return { first: parts[0], last: parts.length > 1 ? parts[parts.length - 1] : "", full };
}

export function firstName(name = "") {
  return splitName(name).first;
}

// mode: "first" -> first name only · "first_last" -> first + surname (drops middles) · else the full name.
export function displayName(name = "", mode = "full") {
  const p = splitName(name);
  if (mode === "first") return p.first;
  if (mode === "first_last") return p.last ? `${p.first} ${p.last}`.trim() : p.first;
  return p.full;
}

// Local calendar date as `YYYY-MM-DD`, tz-safe and locale-independent.
// Use this for MACHINE values: <input type="date"> values (RFC-3339, NOT locale-
// formatted) and API `?date=` params. It reads LOCAL date parts directly, so it is
// never off-by-one in IST near midnight (unlike new Date().toISOString(), which is
// UTC). NOTE: do NOT use a locale string like en-IN here — that yields "29/06/2026",
// which breaks both date inputs and the backend. en-IN is for human DISPLAY only.
export function localISO(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Human-facing date in the Indian locale. Safe on bad input (returns the fallback).
// WS-F4.1 — payer-facing dates are the SCHOOL's calendar day (IST), never the browser's timezone
// (a due date can otherwise render a day early/late abroad or in edge timezones).
export function fmtDateIST(iso, opts = { day: "numeric", month: "short", year: "numeric" }, fallback = "—") {
  if (!iso) return fallback;
  try {
    const d = new Date(String(iso).length === 10 ? `${iso}T00:00:00+05:30` : iso);
    if (Number.isNaN(d.getTime())) return fallback;
    return d.toLocaleDateString("en-IN", { ...opts, timeZone: "Asia/Kolkata" });
  } catch { return fallback; }
}

export function fmtDate(iso, opts = { day: "numeric", month: "short", year: "numeric" }, fallback = "—") {
  if (!iso) return fallback;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? fallback : d.toLocaleDateString("en-IN", opts);
}

export function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
